import { must, supabase } from "./supabase.js";

export type DocumentKind = "XML_RECEIPT" | "PDF" | "FILING_RECEIPT" | "PAYMENT_RECEIPT" | "CIH_CAPTURE" | "TRIBU_CR_DRAFT" | "OTHER";

export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function extensionOf(name: string): string {
  const m = /\.([a-z0-9]{1,8})$/i.exec(name);
  return m ? m[1]!.toLowerCase() : "bin";
}

/**
 * Upload to the private `documents` bucket at <org>/<taxpayer>/<sha256>.<ext>
 * and register it. Re-uploading the same file returns the existing document
 * (dedupe by SHA-256 per taxpayer).
 */
export async function uploadDocument(args: {
  organizationId: string;
  taxpayerId: string;
  file: File;
  kind: DocumentKind;
  title?: string;
  periodKey?: string | null;
  obligationId?: string | null;
}): Promise<{ id: string; duplicate: boolean }> {
  const { organizationId, taxpayerId, file, kind } = args;
  if (file.size > 25 * 1024 * 1024) throw new Error("El archivo supera 25 MB");
  const sha = await sha256Hex(file);
  const existing = must(await supabase.from("documents").select("id").eq("taxpayer_id", taxpayerId).eq("sha256", sha).maybeSingle()) as { id: string } | null;
  if (existing) return { id: existing.id, duplicate: true };
  const path = `${organizationId}/${taxpayerId}/${sha}.${extensionOf(file.name)}`;
  const up = await supabase.storage.from("documents").upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
  if (up.error && !/exists/i.test(up.error.message)) throw new Error(up.error.message);
  const row = must(
    await supabase
      .from("documents")
      .insert({
        organization_id: organizationId,
        taxpayer_id: taxpayerId,
        kind,
        title: args.title?.trim() || file.name,
        period_key: args.periodKey ?? null,
        obligation_id: args.obligationId ?? null,
        storage_path: path,
        sha256: sha,
        size_bytes: file.size,
        mime_type: file.type || "application/octet-stream",
      })
      .select("id")
      .single(),
  ) as { id: string };
  return { id: row.id, duplicate: false };
}

export async function openDocument(storagePath: string): Promise<void> {
  const { data, error } = await supabase.storage.from("documents").createSignedUrl(storagePath, 60);
  if (error) throw new Error(error.message);
  window.open(data.signedUrl, "_blank", "noopener");
}
